import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { Crosshair, BarChart3 } from "lucide-react";
import { fetchPicks } from "@/lib/api";
import PickCard from "@/components/PickCard";
import SectionHeader from "@/components/SectionHeader";
import { TimezoneBar } from "@/components/TimezoneBar";
import FeaturedFreePick from "@/components/home/FeaturedFreePick";
import { Skeleton } from "@/components/ui/skeleton";

const SPORT_TABS = [
  { key: "nba", label: "🏀 NBA", params: { sport: "nba", scope: "upcoming" } },
  { key: "soccer", label: "⚽ Soccer", params: { sport: "soccer", scope: "upcoming" } },
  { key: "upcoming", label: "📅 Upcoming", params: { scope: "upcoming" } },
  { key: "results", label: "📊 Results", params: { scope: "results" } },
];
const FILTERS = [
  { key: "all", label: "All", params: {} },
  { key: "free", label: "Free", params: { visibility: "free" } },
  { key: "vip", label: "VIP", params: { visibility: "vip" } },
  { key: "props", label: "Player Props", params: { market: "player_prop" } },
  { key: "game", label: "Game Picks", params: { market: "game" } },
  { key: "best", label: "Best Bets", params: { best_bet: true } },
];
const SKELETONS = ["a", "b", "c", "d", "e", "f"];
const EMPTY = {
  nba: "No NBA recommendations are published right now. Every selection here is personally researched and approved — nothing is auto-generated.",
  soccer: "No soccer recommendations are published right now. Selections appear here only after personal review and approval.",
  upcoming: "No upcoming recommendations yet. New selections are published after research is complete — never automatically.",
  results: "No settled recommendations yet. Every result will appear here permanently once verified.",
};

function Tab({ active, onClick, children, testid, small }) {
  return (
    <button role="tab" aria-selected={active} data-testid={testid} onClick={onClick}
      className={`shrink-0 snap-start rounded-lg border font-display font-bold uppercase tracking-wider transition-colors min-h-[44px] sm:min-h-0 ${small ? "px-3.5 py-2 text-xs sm:py-1.5" : "px-4 py-2.5 text-sm sm:py-2"} ${
        active ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-[#2A364F] text-slate-400 hover:border-[#00E676]/40 hover:text-white"}`}>
      {children}
    </button>
  );
}

export default function Picks() {
  const [sport, setSport] = useState("upcoming");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const sportTab = SPORT_TABS.find((t) => t.key === sport);
  const filterTab = FILTERS.find((t) => t.key === filter);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["picks", sport, filter],
    queryFn: () => fetchPicks({ ...sportTab.params, ...filterTab.params, limit: 60 }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-20 lg:px-8" data-testid="picks-page">
      <SectionHeader
        eyebrow="The Card"
        title="Upcoming Picks"
        sub="Personally researched recommendations with full written reasoning. Free picks are fully open; VIP picks are locked for members."
        testid="picks-header"
      />

      <div className="-mx-4 mb-3 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:mb-4 sm:flex-wrap sm:overflow-visible sm:px-0" role="tablist" aria-label="Sport">
        {SPORT_TABS.map((t) => <Tab key={t.key} active={sport === t.key} onClick={() => setSport(t.key)} testid={`picks-tab-${t.key}`}>{t.label}</Tab>)}
      </div>
      <div className="-mx-4 mb-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0" role="tablist" aria-label="Pick filters">
        {FILTERS.map((t) => <Tab key={t.key} small active={filter === t.key} onClick={() => setFilter(t.key)} testid={`picks-filter-${t.key}`}>{t.label}</Tab>)}
      </div>
      <div className="mb-6 sm:mb-10"><TimezoneBar /></div>
      {filter !== "vip" && sport !== "results" && sport !== "soccer" && <div className="mb-8 sm:mb-12"><FeaturedFreePick compact /></div>}

      {isLoading ? (
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SKELETONS.map((k) => <Skeleton key={k} className="h-96 rounded-xl bg-[#141A26]" />)}
        </div>
      ) : (data?.picks || []).length === 0 ? (
        <div className="panel flex flex-col items-center px-6 py-20 text-center" data-testid="picks-empty-state">
          <Crosshair size={36} className="text-slate-600" />
          <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-white">No published recommendations</h3>
          <p className="mt-2 max-w-md text-sm text-slate-400">{EMPTY[sport]}</p>
        </div>
      ) : (
        <div className={`grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 ${isFetching ? "opacity-70 transition-opacity" : ""}`}>
          {data.picks.map((p, i) => <PickCard key={p.id} pick={p} index={i} onUnlock={() => navigate("/#pricing")} />)}
        </div>
      )}

      {sport === "results" && (
        <Link to="/results" data-testid="picks-full-results-link" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-[#00E676] hover:underline">
          <BarChart3 size={14} /> Open the full results ledger →
        </Link>
      )}
    </div>
  );
}
